
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children: React.ReactNode;
  maxWidth?: string;
  zIndex?: string; 
}

const Modal: React.FC<ModalProps> = ({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  maxWidth = 'max-w-2xl', 
  zIndex = 'z-[9999]' 
}) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !mounted) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Portalを使用してモーダルをbodyの直下にレンダリングする
  return createPortal(
    <div 
      className={`fixed inset-0 ${zIndex} flex items-center justify-center p-4 sm:p-6 overflow-hidden`}
      aria-modal="true"
      role="dialog"
    >
      {/* 背景（Backdrop）: 画面全体を覆う */}
      <div 
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300" 
        onClick={handleBackdropClick}
      />

      {/* モーダル本体: 画面中央に配置 */}
      <div 
        className={`relative w-full ${maxWidth} bg-white rounded-2xl shadow-2xl transform transition-all flex flex-col max-h-[calc(100vh-40px)] sm:max-h-[calc(100vh-80px)] z-10 animate-in zoom-in-95 fade-in duration-300`}
      >
        {/* ヘッダーエリア */}
        <div className="flex items-center justify-between p-6 pb-2">
          <div className={`${title ? 'border-l-4 border-blue-600 pl-4' : ''} text-lg font-black text-slate-800 leading-tight flex items-center min-h-[28px]`}>
            {title}
          </div>
          <button 
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full p-2 transition-all -mr-2"
            aria-label="閉じる"
          >
            <X size={20} strokeWidth={2.5} />
          </button>
        </div>

        {/* コンテンツエリア: スクロール可能 */}
        <div className="p-6 pt-4 overflow-y-auto custom-scrollbar flex-1">
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default Modal;
